import { ValuesCache } from '@leverege/value-cache';
import { GlobalState } from '@leverege/ui-redux';
import { DataSources } from '@leverege/ui-attributes';
import I18N from '@leverege/i18n';
import { Relationship } from '@leverege/molten-core';
import Groupings from "../../groupings/Groupings.js";
import Constants from "../../groupings/Constants.js";
import ValueSourceUtil from "../../groupings/ValueSourceUtil.js";
import GroupingsUtil from "./GroupingsUtil.js";
const TAG = 'has:grouping';
const {
  tf
} = I18N.ns('molten.imagine.groupings.UrlGroupingsBlocker');

/**
 * This will attempt to find a Grouping object from the current url. It assumes
 * that the LoadHierarchyBlocker is installed.
 */

export default class UrlGroupingsBlocker {
  /** 
   * The blueprint tag indicating that it could have a grouping object
   */
  static TAG = TAG;
  constructor() {
    this.cache = new ValuesCache();
    this.cancelUpdater = null;
    this.updater = null;
  }
  static findRelationshipWithGrouping(rel) {
    if (rel.attribute == null && rel.isItem && rel.blueprint.hasTag(TAG)) {
      return rel;
    }
    let pr = rel.parentRelationship;
    while (pr) {
      if (pr.blueprint.hasTag(TAG)) {
        return pr;
      }
      pr = pr.parentRelationship;
    }
    return null;
  }
  shutdown = () => {
    if (this.cancelUpdater) {
      this.cancelUpdater();
      this.cancelUpdater = null;
    }
  };
  update = () => {
    // eslint-disable-next-line no-console
    this.block(this.cxt, true).catch(console.error);
  };
  block = async (cxt, isUpdate = false) => {
    const {
      relationship,
      match,
      onFilterChange,
      filters
    } = cxt || {};
    this.cxt = cxt;
    if (relationship == null || match == null) {
      GlobalState.dispatch(Groupings.setCurrentGrouping(null, null));
      return null;
    }
    const grpRel = this.cache.calc('rel', UrlGroupingsBlocker.findRelationshipWithGrouping, relationship);
    if (grpRel == null) {
      GlobalState.dispatch(Groupings.setCurrentGrouping(null, null));
      return null;
    }
    const {
      objRef
    } = this.cache.calc('item', Relationship.getObjectRefAndAction, grpRel, match);
    if (objRef == null) {
      GlobalState.dispatch(Groupings.setCurrentGrouping(null, null));
      return null;
    }
    const curr = Groupings.getGrouping(GlobalState.get(), objRef);
    const item = DataSources.getData(objRef);
    let grouping = item?.metadata?.grouping || null;
    let sectionsState = GroupingsUtil.getSectionStateFromFilters(filters);
    if (grouping == null) {
      grouping = grpRel.blueprint.metadata?.grouping;
    }
    // Check the grouping object for any section with auto generated values. 
    if (grouping) {
      // 
      const needsUpdating = await ValueSourceUtil.needsUpdating(objRef, grouping, curr, cxt);
      if (isUpdate !== true && curr && !needsUpdating) {
        const updater = await ValueSourceUtil.getUpdater(objRef, grouping, cxt);
        this.cancelUpdater = updater(this.update);
        GlobalState.dispatch(Groupings.setCurrentGrouping(objRef));
        if (sectionsState) {
          GlobalState.dispatch(Groupings.setSelectedSectionValues(sectionsState, objRef));
        } else {
          sectionsState ||= Groupings.getSelectedSectionValues(GlobalState.get(), objRef.id);
        }
        GroupingsUtil.installFilter({
          sectionsState,
          relationship,
          currentGrouping: curr,
          onFilterChange,
          filterName: 'molten.ImagineGroupingFilter',
          isOnMount: true
        });
        return this.shutdown;
      }

      // convert levels to values. convert null value to missing
      if (grouping.sections) {
        grouping = {
          ...grouping,
          sections: grouping.sections.map((s, index) => {
            if (s.id == null) {
              // eslint-disable-next-line no-console
              console.error('Grouping Section does not have an id', s);
              // eslint-disable-next-line no-param-reassign
              s.id = `s-${index}`;
            }
            const ns = {
              ...s
            };
            ns.values = s.values || s.levels;
            ns.values = ns.values.map((v, idx) => {
              return v.value == null ? {
                ...v,
                value: `${Constants.MISSING_SECTION_VALUE}${idx}`
              } : v;
            });
            delete ns.levels;
            return ns;
          })
        };
      }
      const resolve = await ValueSourceUtil.resolve(objRef, grouping, cxt);
      grouping = resolve.grouping;
      if (resolve.updater && !isUpdate) {
        // Start listening for updates, grab the
        this.cancelUpdater = resolve.updater(this.update);
      }
    }
    // Note, Aggregations might need added filtering from their view, so
    // we will delay that for a hidden DataViewer (AggLoader)
    const currentGrouping = {
      aggRequestorType: 'imagine.blueprint.AggRequestor',
      ...grouping
    };
    GlobalState.dispatch(Groupings.setCurrentGrouping(objRef, currentGrouping));
    if (sectionsState) {
      GlobalState.dispatch(Groupings.setSelectedSectionValues(sectionsState, objRef));
    } else {
      sectionsState ||= Groupings.getSelectedSectionValues(GlobalState.get(), objRef.id);
    }
    GroupingsUtil.installFilter({
      sectionsState,
      relationship,
      currentGrouping,
      onFilterChange,
      filterName: 'molten.ImagineGroupingFilter',
      isOnMount: true
    });
    return this.shutdown;
  };
}
UrlGroupingsBlocker.createPlugin = () => {
  return {
    id: 'molten.blueprints.groupings.UrlGroupingsBlocker',
    name: tf('name'),
    sort: 1,
    // load after the hierarchy
    matches: {
      client: '!ItemScreen',
      path: '*'
    },
    execute: cxt => {
      const blocker = new UrlGroupingsBlocker();
      return blocker.block(cxt);
    }
  };
};